import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {OptionStatusIndicator} from 'platform/components';

import {useCallback, useMemo} from 'react';

import {useGetUserAvailabilitySummaryQuery, useGetUsersQuery} from '@dms/api/customer';
import {UserStateFilterSchema} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';

import {UserAbsenceTooltip} from '../components/UserAbsenceTooltip/UserAbsenceTooltip';

const USER_AVAILABILITY_POLLING_INTERVAL = 60_000;

type UseUserAbsenceIndicatorsParams = {
  isEnabled?: boolean;
};

/**
 * Central source of the user absence indicator (red dot, T20-125018 Story 5).
 * The red dot (`hasAbsence`) is visible to everyone; the tooltip detail is present
 * only when BE returns `userAbsence`, which is ACL-filtered per target user on BE.
 */
export function useUserAbsenceIndicators(params?: UseUserAbsenceIndicatorsParams) {
  const isEnabled = (params?.isEnabled ?? true) && isFeatureEnabled(featureFlags.USER_ABSENCE);

  const {data: users, isLoading: isUsersLoading} = useGetUsersQuery(
    {stateFilter: UserStateFilterSchema.enum.active},
    {skip: !isEnabled}
  );

  const userIds = useMemo(
    () => users?.filter((user) => !user.blocked).map((user) => user.id) ?? [],
    [users]
  );

  // Absences start and end on their own schedule or get cut short in the absent user's browser,
  // and no push event reaches anyone else, so the summary refreshes itself. Every consumer shares
  // this one entry: one request per tick or focus, however many pickers are mounted.
  const {data: availabilitySummary, isLoading: isSummaryLoading} =
    useGetUserAvailabilitySummaryQuery(isEnabled && userIds.length > 0 ? {userIds} : skipToken, {
      pollingInterval: USER_AVAILABILITY_POLLING_INTERVAL,
      skipPollingIfUnfocused: true,
      refetchOnFocus: true,
    });

  const summaryByUserId = useMemo(
    () => new Map(availabilitySummary?.map((summary) => [summary.userId, summary])),
    [availabilitySummary]
  );

  const getStatusIndicator = useCallback(
    (userId: string): OptionStatusIndicator | undefined => {
      const summary = summaryByUserId.get(userId);

      if (!summary?.hasAbsence) {
        return undefined;
      }

      return {
        tooltip: summary.userAbsence ? (
          <UserAbsenceTooltip absence={summary.userAbsence} />
        ) : undefined,
      };
    },
    [summaryByUserId]
  );

  return {
    getStatusIndicator,
    isLoading: isEnabled && (isUsersLoading || isSummaryLoading),
  };
}
